import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Activity, Brain, Heart, ArrowRight, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface UnifiedScoreCardProps {
  value: number;
  statusLabel: string;
  mentalScore: number | null;
  physicalScore: number | null;
  recoveryScore: number | null;
}

export const UnifiedScoreCard: React.FC<UnifiedScoreCardProps> = ({
  value,
  statusLabel,
  mentalScore,
  physicalScore,
  recoveryScore
}) => {
  const navigate = useNavigate();
  const [showInfo, setShowInfo] = useState(false);

  const getStatusGradient = (status: string) => {
    if (status.includes('optimal') || status.includes('thriving')) 
      return 'from-accent/15 via-primary/10 to-secondary/10';
    if (status.includes('good')) 
      return 'from-primary/15 via-accent/10 to-secondary/10';
    if (status.includes('moderate')) 
      return 'from-secondary/15 via-muted/10 to-primary/5';
    return 'from-muted/20 via-muted/15 to-muted/10';
  };

  const getBadgeStyle = (status: string) => {
    if (status.includes('optimal') || status.includes('thriving')) 
      return 'bg-accent/15 text-accent border-accent/30 shadow-sm';
    if (status.includes('good')) 
      return 'bg-primary/15 text-primary border-primary/30 shadow-sm';
    if (status.includes('moderate')) 
      return 'bg-secondary/15 text-secondary border-secondary/30 shadow-sm';
    return 'bg-muted/20 text-muted-foreground border-muted/30';
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return 'text-muted-foreground';
    if (score >= 75) return 'text-accent';
    if (score >= 50) return 'text-primary';
    if (score >= 25) return 'text-secondary';
    return 'text-muted-foreground';
  };

  return (
    <Card className={`relative overflow-hidden border-0 bg-gradient-to-br ${getStatusGradient(statusLabel)} backdrop-blur-sm shadow-lg`}>
      {/* Subtle decorative element */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-radial from-primary/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <CardContent className="p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <span className="text-sm font-medium text-foreground tracking-wide">Your Wellness Today</span>
          </div>
          <Badge variant="outline" className={`${getBadgeStyle(statusLabel)} font-medium text-xs tracking-wider`}>
            {statusLabel.replace('-', ' ').toUpperCase()}
          </Badge>
        </div>

        {/* Main Score Display */}
        <div className="flex flex-col items-center mb-4">
          <div className="relative">
            <span className="text-5xl sm:text-6xl font-medium text-primary tracking-tight">{Math.round(value)}</span>
            <span className="absolute -top-1 -right-5 text-sm text-foreground/70 font-normal">/100</span>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <Progress value={value} className="h-1.5 bg-muted/30" />
        </div>

        {/* Score Breakdown */}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          {physicalScore !== null && (
            <div className="text-center space-y-1.5 p-2 rounded-lg bg-accent/10 border border-accent/20">
              <div className="flex items-center justify-center gap-1">
                <Activity className="w-3.5 h-3.5 text-accent" />
                <span className="text-xs font-medium text-foreground/80 uppercase tracking-wide">Physical</span>
              </div>
              <div className={`text-xl sm:text-2xl font-bold tracking-tight ${getScoreColor(physicalScore)}`}>
                {Math.round(physicalScore)}
              </div>
              <Progress value={physicalScore} className="h-1 bg-muted/30" />
            </div>
          )}
          
          {mentalScore !== null && (
            <div className="text-center space-y-1.5 p-2 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center justify-center gap-1">
                <Brain className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium text-foreground/80 uppercase tracking-wide">Mental</span>
              </div>
              <div className={`text-xl sm:text-2xl font-bold tracking-tight ${getScoreColor(mentalScore)}`}>
                {Math.round(mentalScore)}
              </div>
              <Progress value={mentalScore} className="h-1 bg-muted/30" />
            </div>
          )}
          
          {recoveryScore !== null && (
            <div className="text-center space-y-1.5 p-2 rounded-lg bg-secondary/10 border border-secondary/20">
              <div className="flex items-center justify-center gap-1">
                <Heart className="w-3.5 h-3.5 text-secondary" />
                <span className="text-xs font-medium text-foreground/80 uppercase tracking-wide">Recovery</span>
              </div>
              <div className={`text-xl sm:text-2xl font-bold tracking-tight ${getScoreColor(recoveryScore)}`}>
                {Math.round(recoveryScore)}
              </div>
              <Progress value={recoveryScore} className="h-1 bg-muted/30" />
            </div>
          )}
        </div>

        {/* Info Explainer */}
        <Collapsible open={showInfo} onOpenChange={setShowInfo} className="mb-3">
          <CollapsibleTrigger asChild>
            <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full justify-center">
              <Info className="w-3.5 h-3.5" />
              <span>How is this calculated?</span>
              {showInfo ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3">
            <div className="bg-muted/20 rounded-lg p-3 text-xs text-muted-foreground space-y-2">
              <p className="font-medium text-foreground/80">Your score combines three key areas:</p>
              <ul className="space-y-1.5 ml-1">
                <li className="flex items-start gap-2">
                  <Brain className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground/80">Mental (40%):</strong> Mood check-ins, emotional state, daily reflections</span>
                </li>
                <li className="flex items-start gap-2">
                  <Activity className="w-3.5 h-3.5 text-accent mt-0.5 shrink-0" />
                  <span><strong className="text-foreground/80">Physical (35%):</strong> Inflammation markers, sleep quality, activity levels</span>
                </li>
                <li className="flex items-start gap-2">
                  <Heart className="w-3.5 h-3.5 text-secondary mt-0.5 shrink-0" />
                  <span><strong className="text-foreground/80">Recovery (25%):</strong> Rest quality, HRV data, and recuperation</span>
                </li>
              </ul>
              <p className="text-[10px] italic pt-1">Complete daily check-ins and connect wearables for more accurate scores.</p>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* View Details Button */}
        <Button 
          variant="ghost" 
          className="w-full group hover:bg-primary/10 transition-all duration-300" 
          onClick={() => navigate('/my-scores')}
        >
          <span className="font-medium tracking-wide">View Detailed Scores</span>
          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </CardContent>
    </Card>
  );
};